<template>
  <component
    :is="type"
    :to="to"
    :href="href"
    class="btn"
    :disabled="disabled || loading"
    @click="onClick"
  >
    <slot v-if="!loading" name="icon-start" />

    <slot />

    <slot v-if="!loading" name="icon-end" />

    <v-icon v-if="loading" name="spinner" spin class="loader" />
  </component>
</template>

<script>
export default {
  name: "ButtonLink",
  props: {
    href: { default: "", type: String },
    to: { default: "", type: String },
    primary: Boolean,
    disabled: { default: false, type: Boolean },
    loading: Boolean,
  },
  computed: {
    type() {
      if (this.to) {
        return "router-link";
      }

      if (this.href) {
        return "a";
      }

      return "button";
    },
  },
  methods: {
    onClick(e) {
      if (this.disabled) return e.preventDefault();

      this.$emit("click", e);
    },
  },
};
</script>

<style scoped>
.loader {
  @apply ml-1 align-middle;
  margin-top: -1px;
}

.btn {
  @apply font-black py-1 px-4 rounded underline outline-none text-support flex items-center text-xs;
  font-family: "Lato";

  &:hover {
    @apply no-underline cursor-pointer;
  }

  &[disabled] {
    @apply bg-grey border-grey;
    cursor: not-allowed;
    opacity: 0.7;
    text-shadow: none;

    &:hover {
      @apply bg-grey border-grey no-underline;
      text-shadow: none;
    }

    >>> svg {
      filter: none !important;
    }
  }

  @screen sm {
    @apply text-sm;
  }

  @screen md {
    @apply py-2 text-base;
  }
}

.sm {
  @apply btn text-xs py-1 px-3;

  @screen sm {
    @apply text-sm;
  }

  @screen md {
    @apply py-1 text-sm;
  }

  .loader {
    font-size: 10px;
  }
}

.xs {
  @apply btn text-xs leading-tight py-1 px-2;

  @screen sm {
    @apply text-xs;
  }

  @screen md {
    @apply py-1 text-xs;
  }

  .loader {
    font-size: 10px;
  }
}

.xxs {
  @apply btn py-0 px-2;
  font-size: 12px;

  @screen sm {
    font-size: 12px;
  }

  @screen md {
    @apply py-0;
    font-size: 12px;
  }

  .loader {
    font-size: 10px;
  }
}

.link {
  @apply font-normal no-underline shadow-none;
  font-family: inherit;

  &:hover {
    @apply underline;
  }

  &:focus {
    @apply shadow-none;
  }

  &[disabled] {
    @apply bg-transparent border-0;
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      @apply bg-transparent border-0;
    }
  }
}

.grey {
  @apply border-2 border-support bg-support text-white no-underline shadow;
  text-shadow: 0px 1px 3px #333;

  &:hover {
    @apply bg-support-dark border-support-dark;
    text-shadow: 0px 1px 3px #999;
  }
}

.white {
  @apply bg-white border-2 border-white text-grey-dark no-underline;

  &:hover {
    @apply bg-grey-dark text-white border-grey-dark;
  }
}

.primary {
  @apply bg-success border-success border-2 text-white no-underline shadow;

  text-shadow: 0px 1px 3px #326536;

  >>> svg {
    filter: drop-shadow(0px 1px 2px #326536);
  }

  &:hover {
    @apply bg-brand border-brand;
    text-shadow: 0px 1px 3px #353a61;

    >>> svg {
      filter: drop-shadow(0px 1px 2px #353a61);
    }
  }

  &:focus {
    @apply shadow-inner;
  }
}

.secondary {
  @apply bg-brand border-2 border-brand text-white no-underline shadow;
  text-shadow: 0px 1px 3px #353a61;

  >>> svg {
    filter: drop-shadow(0px 1px 1px #353a61);
  }

  &:hover {
    @apply bg-brand-dark border-brand-dark;
    text-shadow: 0px 1px 3px #181e49;
  }

  &:focus {
    @apply shadow-inner;
  }
}

.alt {
  @apply bg-brand-alt border-2 border-brand-alt text-white no-underline shadow;
  text-shadow: 0px 1px 3px #d6905d;

  >>> svg {
    filter: drop-shadow(0px 1px 2px #d6905d);
  }

  &:hover {
    @apply bg-brand border-brand text-white;
    text-shadow: 0px 1px 3px #181e49;

    >>> svg {
      filter: drop-shadow(0px 1px 1px #353a61);
    }
  }

  &:focus {
    @apply shadow-inner;
  }
}

.danger {
  @apply bg-danger border-2 border-danger text-white no-underline shadow;
  text-shadow: 0px 1px 3px #702a31;

  >>> svg {
    filter: drop-shadow(0px 1px 2px #702a31);
  }

  &:hover {
    @apply bg-danger-dark border-danger-dark text-white;
    text-shadow: 0px 1px 3px #702a31;

    >>> svg {
      filter: drop-shadow(0px 1px 1px #353a61);
    }
  }

  &:focus {
    @apply shadow-inner;
  }
}

.outline {
  @apply bg-transparent text-brand border-2 border-brand no-underline shadow;

  &:hover {
    @apply bg-grey-lightest border-success text-success;
  }

  &:focus {
    @apply shadow-inner;
  }

  &[disabled] {
    @apply bg-transparent text-brand border-brand;
    opacity: 0.5;

    &:hover {
      @apply bg-transparent text-brand border-brand;
    }
  }
}
</style>
